import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { HTS_ASSISTANT_ANSWER_MAX_LENGTH, HTS_ASSISTANT_ENGLISH_HINT_MS } from '../../const/hts-assistant.const';

/**
 * Answer input: Enter sends, Shift+Enter inserts a newline, grows with the text (#13383).
 * On touch screens Enter only inserts a newline and the send button is the single way to send (#13410).
 * The classifier works in English only (product decision 2026-10-12): characters outside printable ASCII are dropped
 * as they are typed or pasted, and a neutral hint explains why for a few seconds.
 */
@Component({
  selector: 'app-hts-assistant-composer',
  templateUrl: './hts-assistant-composer.component.html',
  styleUrls: ['./hts-assistant-composer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HtsAssistantComposerComponent implements OnDestroy {
  /** Anything but printable ASCII, tab and line breaks. */
  private static readonly nonEnglish: RegExp = /[^\x20-\x7E\t\n\r]/g;
  /** Typographic characters that have an ASCII twin: normalised instead of dropped, so words are not glued together. */
  private static readonly asciiTwins: Array<[RegExp, string]> = [
    [/[\u00A0\u1680\u2000-\u200A\u202F\u205F\u3000]/g, ' '],
    [/[\u2010-\u2015\u2212]/g, '-'],
    [/[\u2018\u2019\u201A\u2032]/g, '\u0027'],
    [/[\u201C\u201D\u201E\u2033]/g, '"'],
    [/\u2026/g, '...'],
  ];

  private static toEnglish(text: string): string {
    const normalised: string = HtsAssistantComposerComponent.asciiTwins.reduce(
      (value: string, [pattern, twin]: [RegExp, string]) => value.replace(pattern, twin),
      text,
    );
    return normalised.replace(HtsAssistantComposerComponent.nonEnglish, '');
  }

  /** Disabling the textarea (a turn in flight) drops the browser focus; after a send it is brought back once re-enabled. */
  @Input()
  public set isDisabled(isDisabled: boolean) {
    const wasDisabled: boolean = this.isDisabledValue;
    this.isDisabledValue = isDisabled;
    if (wasDisabled && !isDisabled && this.isRefocusPending) {
      this.isRefocusPending = false;
      // the DOM attribute follows this change detection pass: focus once it is gone
      setTimeout(() => this.focus());
    }
  }
  public get isDisabled(): boolean {
    return this.isDisabledValue;
  }
  @Input() public maxLength: number = HTS_ASSISTANT_ANSWER_MAX_LENGTH;
  @Output() public send: EventEmitter<string> = new EventEmitter<string>();

  @ViewChild('input', { static: true })
  public input: ElementRef<HTMLTextAreaElement>;

  public value: string = '';
  public isEnglishOnlyHint: boolean = false;
  /** Coarse pointer (phone / tablet): decided once, the device does not change while the drawer is open. */
  public readonly isTouch: boolean = typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(pointer: coarse)').matches;

  private readonly maxHeightPx: number = 110;
  private hintTimer: ReturnType<typeof setTimeout> | null = null;
  private isDisabledValue: boolean = false;
  private isRefocusPending: boolean = false;

  constructor(private cdr: ChangeDetectorRef) {}

  public ngOnDestroy(): void {
    this.clearHintTimer();
  }

  public get isCanSend(): boolean {
    return !this.isDisabled && !!this.value.trim();
  }

  /**
   * Typing and pasting both land here; blocked characters are removed in place with the caret kept where it was.
   * Not during an IME composition (dead keys, mobile keyboards): rewriting the value would cancel it — `compositionend`
   * fires one more input event once the composition is committed.
   */
  public onInput(event: Event): void {
    const textarea: HTMLTextAreaElement = event.target as HTMLTextAreaElement;
    const raw: string = textarea.value;
    if ((event as InputEvent).isComposing) {
      this.value = raw;
      this.autosize(textarea);
      return;
    }
    const clean: string = HtsAssistantComposerComponent.toEnglish(raw);
    if (clean !== raw) {
      const caret: number = textarea.selectionStart;
      const keptBeforeCaret: number = HtsAssistantComposerComponent.toEnglish(raw.slice(0, caret)).length;
      textarea.value = clean;
      textarea.setSelectionRange(keptBeforeCaret, keptBeforeCaret);
      this.showEnglishOnlyHint();
    }
    this.value = clean;
    this.autosize(textarea);
  }

  public onCompositionEnd(event: Event): void {
    this.onInput(event);
  }

  public onKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter' || event.shiftKey || event.isComposing || this.isTouch) {
      return;
    }
    event.preventDefault();
    this.onSend();
  }

  /** Emits and clears at once: the message is shown optimistically by the drawer; on failure it is put back via `setValue`. */
  public onSend(): void {
    if (!this.isCanSend) {
      return;
    }
    const text: string = HtsAssistantComposerComponent.toEnglish(this.value).trim();
    this.clear();
    this.isRefocusPending = true;
    if (text) {
      this.send.emit(text);
    }
  }

  public clear(): void {
    this.setValue('');
  }

  /** Restores text after a failed send so the user does not retype it. */
  public setValue(text: string): void {
    this.value = text || '';
    // text given back after a refused send: the pending refocus belongs to that send, not to a later re-enable
    if (this.value) {
      this.isRefocusPending = false;
    }
    if (this.input && this.input.nativeElement) {
      this.input.nativeElement.value = this.value;
      this.autosize(this.input.nativeElement);
    }
    this.cdr.markForCheck();
  }

  /** No-op on touch screens: re-focusing would pop the keyboard up again. */
  public focus(): void {
    if (this.input && this.input.nativeElement && !this.isDisabled && !this.isTouch) {
      this.input.nativeElement.focus();
    }
  }

  private showEnglishOnlyHint(): void {
    this.clearHintTimer();
    this.isEnglishOnlyHint = true;
    this.hintTimer = setTimeout(() => {
      this.isEnglishOnlyHint = false;
      this.hintTimer = null;
      this.cdr.markForCheck();
    }, HTS_ASSISTANT_ENGLISH_HINT_MS);
    this.cdr.markForCheck();
  }

  private clearHintTimer(): void {
    if (this.hintTimer !== null) {
      clearTimeout(this.hintTimer);
      this.hintTimer = null;
    }
  }

  private autosize(textarea: HTMLTextAreaElement): void {
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, this.maxHeightPx)}px`;
  }
}
